// src/components/patient/overview/hooks/useDashboard.ts
import { useState, useEffect, useCallback } from 'react';
import { CertificationPeriodsService } from '../../../../services/patientmr/certificationPeriods.service';
import type { PatientDashboardData } from '../../../../dtos/patientmr/certificationPeriod.dto';
import { usePatient } from '../../../../context/patientContext';

export interface DashboardSource {
  data: PatientDashboardData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void> | void;
}

/**
 * The patient dashboard for the Overview. Overview v3 (OV-13): the chart header already loads it
 * (usePatientChartSummary), so the patient page passes that copy as `external` and no second
 * request is made. Without `external` (other screens, tests) the hook loads its own.
 * The refresh function is registered in PatientContext so other tabs can reload the dashboard.
 */
export function useDashboard(patientId: string, external?: DashboardSource): DashboardSource {
  const { setRefreshDashboard } = usePatient();
  const hasExternal = Boolean(external);
  const [data, setData] = useState<PatientDashboardData | null>(null);
  const [loading, setLoading] = useState(!hasExternal);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!patientId || hasExternal) return;
    setLoading(true);
    setError(null);
    try {
      const result = await CertificationPeriodsService.getDashboard(patientId);
      setData(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [patientId, hasExternal]);

  const externalRefresh = external?.refresh;
  const refresh = externalRefresh ?? fetch;

  // Double-wrap required: useState calls a function directly if given one.
  useEffect(() => {
    setRefreshDashboard(() => refresh);
    return () => setRefreshDashboard(null);
  }, [refresh, setRefreshDashboard]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return external ?? { data, loading, error, refresh: fetch };
}
